import { useState, useMemo, useEffect, useCallback } from 'react';
import useSessionType from '@hooks/useSessionType';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import useCallStatus from '@hooks/useCallStatus';
import { useIsAudioDisabled, useIsVideoDisabled } from '@components/Clinicians/Session/Components/CallMenu';
import { useSessionAppointmentId } from './useSessionAppointmentId';

enum CallWindowState {
  Collapsed = 'collapsed',
  Expanded = 'expanded',
  Hidden = 'hidden'
}

export default function usePatientStatusPanel() {
  const { appointment } = useSessionAppointment();
  const { appointment_completed } = appointment;
  const session_appointment_id = useSessionAppointmentId();

  const { detailsCurrentAppointment } = useAppointmentDetails(session_appointment_id);
  const isAppointmentV1 = detailsCurrentAppointment?.appt_version === 1;
  const isAppointmentV2 = detailsCurrentAppointment?.appt_version === 2;

  const sessionType = useSessionType();
  const isVideoSessionType = sessionType && sessionType === 'VIDEO';
  const isAudioSessionType = sessionType && sessionType === 'AUDIO';

  const callWindowStateKey = `callWindowState[${session_appointment_id}]` as const;
  const [callWindowState, setCallWindowState] = useState(CallWindowState.Hidden);
  useEffect(() => sessionStorage.setItem(callWindowStateKey, callWindowState), [callWindowState, callWindowStateKey]);

  const [initialised, setInitialised] = useState(false);

  const setInitialWindowState = useCallback(
    (callWindowState: CallWindowState) => {
      if (initialised) return;

      setCallWindowState(callWindowState);
      setInitialised(true);
    },
    [initialised]
  );

  useEffect(() => {
    // set default value for call window state

    if (!detailsCurrentAppointment) return;

    if (appointment_completed || (isAppointmentV1 && !isVideoSessionType)) {
      setInitialWindowState(CallWindowState.Hidden);
      return;
    }
    if (isVideoSessionType) {
      setInitialWindowState(CallWindowState.Expanded);
      return;
    }

    setInitialWindowState(CallWindowState.Collapsed);
  }, [appointment_completed, detailsCurrentAppointment, isAppointmentV1, isVideoSessionType, setInitialWindowState]);

  const canCallWindowBeCollapsed = isAppointmentV2;
  const showCallMenu = isAppointmentV2;

  const callStatus = useCallStatus(appointment);

  const isCallWindowHidden = useMemo(() => callWindowState === CallWindowState.Hidden, [callWindowState]);
  const isCallWindowCollapsed = useMemo(() => callWindowState === CallWindowState.Collapsed, [callWindowState]);
  const isCallWindowExpanded = useMemo(() => callWindowState === CallWindowState.Expanded, [callWindowState]);

  const isCollapseButtonDisabled = callStatus === 'in-progress' && isCallWindowExpanded;
  const isQuickVideoCallButtonDisabled = useIsVideoDisabled() || callStatus === 'in-progress';
  const isQuickAudioCallButtonDisabled = useIsAudioDisabled();

  const onCollapseCallWindow = () => {
    if (callWindowState === CallWindowState.Collapsed) return;

    setCallWindowState(CallWindowState.Collapsed);
  };

  const onExpandCallWindow = () => {
    if (callWindowState === CallWindowState.Expanded) return;

    setCallWindowState(CallWindowState.Expanded);
  };

  const onSwitchCallWindow = () => {
    setCallWindowState(
      callWindowState === CallWindowState.Collapsed ? CallWindowState.Expanded : CallWindowState.Collapsed
    );
  };

  useEffect(() => {
    if (callStatus === 'in-progress' && isCallWindowCollapsed) onExpandCallWindow();
  }, [callStatus, isCallWindowCollapsed, onExpandCallWindow]);

  return {
    callWindowState,
    isCallWindowHidden,
    isCallWindowCollapsed,
    isCallWindowExpanded,
    canCallWindowBeCollapsed,
    isAudioSessionType,
    isVideoSessionType,
    showCallMenu,
    isCollapseButtonDisabled,
    isQuickVideoCallButtonDisabled,
    isQuickAudioCallButtonDisabled,
    onCollapseCallWindow,
    onExpandCallWindow,
    onSwitchCallWindow
  };
}
